import { createMemo, For, Show, type JSX } from 'solid-js';
import type { PricePoint } from '@lib/supabase/priceHistory';

// Lightweight, dependency-free price-history line chart (inline SVG).
// One pharmacy series at a time — medical patients are registered to a
// single pharmacy, so the strain page picks their default (falling back to
// IPS). Fully dark-mode aware via Tailwind `dark:` variants so it reads
// well in low-light: grid lines, axis text, the line, the area fill and
// the points each carry a dark counterpart.

const W = 320;
const H = 150;
const PAD = { top: 14, right: 14, bottom: 24, left: 38 };
const INNER_W = W - PAD.left - PAD.right;
const INNER_H = H - PAD.top - PAD.bottom;

function shortDate(iso: string): string {
  // en-GB short label, e.g. "03 Jun". Guard against an unparseable value.
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return new Date(t).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

export default function PriceHistoryChart(props: {
  points: PricePoint[];
  pharmacySlug: string;
  pharmacyName: string;
}): JSX.Element {
  const series = createMemo(() =>
    props.points
      .filter((p) => p.pharmacy_slug === props.pharmacySlug)
      .slice()
      .sort((a, b) => a.recorded_at.localeCompare(b.recorded_at)),
  );

  const latest = () => {
    const s = series();
    return s.length ? s[s.length - 1]!.price_per_gram_gbp : null;
  };

  const model = createMemo(() => {
    const s = series();
    if (s.length < 2) return null;
    const prices = s.map((p) => p.price_per_gram_gbp);
    const times = s.map((p) => Date.parse(p.recorded_at));
    let minP = Math.min(...prices);
    let maxP = Math.max(...prices);
    if (minP === maxP) {
      // Flat history — give the line some breathing room so it sits mid-box.
      minP -= 1;
      maxP += 1;
    }
    const minT = Math.min(...times);
    const spanT = Math.max(...times) - minT || 1;
    const x = (t: number) => PAD.left + ((t - minT) / spanT) * INNER_W;
    const y = (p: number) => PAD.top + (1 - (p - minP) / (maxP - minP)) * INNER_H;

    const pts = s.map((p) => ({
      x: x(Date.parse(p.recorded_at)),
      y: y(p.price_per_gram_gbp),
      price: p.price_per_gram_gbp,
      recorded_at: p.recorded_at,
    }));
    const baseline = PAD.top + INNER_H;
    const line = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    const area =
      `M ${pts[0]!.x.toFixed(1)},${baseline} ` +
      pts.map((p) => `L ${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') +
      ` L ${pts[pts.length - 1]!.x.toFixed(1)},${baseline} Z`;
    const ticks = [maxP, (maxP + minP) / 2, minP].map((v) => ({ v, y: y(v) }));
    return { pts, line, area, ticks, first: s[0]!, last: s[s.length - 1]! };
  });

  return (
    <div class="rounded-xl border border-brand-hex bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]">
      <div class="flex items-baseline justify-between gap-2">
        <h3 class="font-display font-semibold dark:text-brand-cream">Price history</h3>
        <Show when={latest() != null}>
          <span class="text-xs text-brand-green/70 dark:text-brand-cream/60">
            now £{latest()!.toFixed(2)}/g
          </span>
        </Show>
      </div>

      <Show
        when={model()}
        fallback={
          <p class="mt-2 text-xs text-brand-green/70 dark:text-brand-cream/60">
            <Show
              when={latest() != null}
              fallback="No price history recorded yet — check back after the next stock update."
            >
              Only one price point so far (£{latest()!.toFixed(2)}/g). The trend line appears once
              the price changes.
            </Show>
          </p>
        }
      >
        {(m) => (
          <svg
            viewBox={`0 0 ${W} ${H}`}
            class="mt-2 w-full h-auto"
            role="img"
            aria-label={`Price per gram over time at ${props.pharmacyName}`}
          >
            {/* Horizontal grid lines + £ labels */}
            <For each={m().ticks}>
              {(t) => (
                <>
                  <line
                    x1={PAD.left}
                    x2={W - PAD.right}
                    y1={t.y}
                    y2={t.y}
                    class="stroke-brand-hex dark:stroke-white/10"
                    stroke-width="1"
                  />
                  <text
                    x={PAD.left - 6}
                    y={t.y}
                    text-anchor="end"
                    dominant-baseline="middle"
                    class="fill-brand-green/60 text-[9px] dark:fill-brand-cream/50"
                  >
                    £{t.v.toFixed(2)}
                  </text>
                </>
              )}
            </For>

            {/* Area fill under the line */}
            <path d={m().area} class="fill-brand-moss/10 dark:fill-emerald-400/10" />

            {/* The price line */}
            <polyline
              points={m().line}
              fill="none"
              class="stroke-brand-moss dark:stroke-emerald-400"
              stroke-width="2"
              stroke-linejoin="round"
              stroke-linecap="round"
            />

            {/* Points */}
            <For each={m().pts}>
              {(p) => (
                <circle
                  cx={p.x}
                  cy={p.y}
                  r="2.5"
                  class="fill-brand-green dark:fill-emerald-300"
                >
                  <title>
                    {shortDate(p.recorded_at)} · £{p.price.toFixed(2)}/g
                  </title>
                </circle>
              )}
            </For>

            {/* X-axis end labels */}
            <text
              x={PAD.left}
              y={H - 6}
              text-anchor="start"
              class="fill-brand-green/60 text-[9px] dark:fill-brand-cream/50"
            >
              {shortDate(m().first.recorded_at)}
            </text>
            <text
              x={W - PAD.right}
              y={H - 6}
              text-anchor="end"
              class="fill-brand-green/60 text-[9px] dark:fill-brand-cream/50"
            >
              {shortDate(m().last.recorded_at)}
            </text>
          </svg>
        )}
      </Show>

      <p class="mt-1 text-[10px] text-brand-green/60 dark:text-brand-cream/50">
        Advertised price/g at {props.pharmacyName}.
      </p>
    </div>
  );
}
